"use client";

import { useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { useMatches } from "@/hooks/useMatches";
import { LIVE_STATUSES } from "@/contants";
import { colorOddsChanges, formatCorrectScoresForGrid, splitCorrectScoresListToSections } from "@/utils/matchInfo";
import BoltKbvMatchMinute from "../BoltKbvMatchMinute";
import BoltOtherBetsDropdown from "../BoltOtherBetsDropdown/BoltOtherBetsDropdown";
import ViuGoTipsAiModal from "../../ViuGoTipsAiModal";
import ICON_ARROW_UP from "../../../assets/images/arrow-up.svg";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";
import "./index.css";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function BoltMatch({ match, leagueName, index }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const { getMatchOdds, getMatchInsight } = useMatches();

  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    currentHomeScore,
    currentAwayScore,
    homeYellow,
    homeRed,
    awayYellow,
    awayRed,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const isEven = index % 2 === 0;
  const bgColor = isEven ? "md:bg-[var(--theme-bg)]" : "md:bg-[var(--theme-bg-muted)]";

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match?.status ?? 0);
  const isLive = LIVE_STATUSES.includes(statusNum);

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;

  const getOddsColor = (odds) => {
    const val = Number(odds);
    if (!odds || isNaN(val)) return "kbv-v26-odds-default";
    if (val < 0) return "kbv-v26-odds-down";
    return "kbv-v26-odds-up";
  };

  const getMobileOddsColor = (odds) => {
    const val = Number(odds);
    if (!odds || isNaN(val)) return "kbv-v26-odds-default";
    if (val < 0) return "kbv-v26-odds-m-down";
    return "kbv-v26-odds-m-up";
  };

  const getFinalOddsColor = (trend, odds, mobileColor) => {
    const trendColor = colorOddsChanges(trend);
    if (trendColor && trendColor !== "text-[var(--theme-text)]") {
      return trendColor;
    }
    return mobileColor || getOddsColor(odds);
  };

  const renderTrendArrow = (trend) => {
    if (trend === "increased" || trend === "up" || trend === 1 || trend === "1") {
      return (
        <span
          className="shrink-0 inline-block w-[10px] h-[10px] lu-odds-status--up"
          aria-hidden="true"
        />
      );
    }
    if (trend === "decreased" || trend === "down" || trend === 2 || trend === "2") {
      return (
        <span
          className="shrink-0 inline-block w-[10px] h-[10px] lu-odds-status--down"
          aria-hidden="true"
        />
      );
    }
    return null;
  };

  const handleOtherOddsClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight || !match?.id) return;
    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const correctScoresListFull = otherOddsData?.correctScoresOdds ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds) : [];
  const correctScoresFull = splitCorrectScoresListToSections(correctScoresListFull);
  const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds) ? otherOddsData.totalCornersOdds : [];
  const hasOtherData = (otherOddsData?.goalOddEvenOdds?.length > 0) || (correctScoresListFull.length > 0) || (totalCornersOdds.length > 0);

  const TZ = "Asia/Ho_Chi_Minh";
  const matchTimeStr =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("HH:mm")
      : "--";
  const matchDateStr =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("DD/MM/YYYY")
      : "--";
  const matchDateShort =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("DD/MM")
      : "--";

  const derivedLeagueName = leagueName || match?.league?.name || match?.leagueName || "";
  const derivedLeagueLogo = match?.league?.logo || match?.leagueLogo || "";
  const homeName = match?.homeTeam?.nameVi || match?.homeName;
  const awayName = match?.awayTeam?.nameVi || match?.awayName;
  const scoreText = `${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`;
  const modalStatusLabel = !isLive ? "Sắp diễn ra" : "Trực tiếp";

  return (
    <div className={`w-full transition-all ${bgColor} hover:bg-[var(--theme-accent)]`}>
      <div className={`hidden md:grid grid-cols-[13%_11%_23%_13%_13%_14%_1fr] items-center gap-x-[20px] px-2 pt-3 pb-2`}>
        <div className="flex items-center justify-center text-center h-full overflow-hidden">
          <span className="text-sm font-bold text-[var(--theme-text)] truncate">
            {leagueName || match?.leagueName || "--"}
          </span>
        </div>

        <div className="flex flex-col items-center justify-center gap-4 h-full">
          {isLive ? (
            <div className="flex flex-col items-center gap-5">
              <span className="flex items-center gap-1 kbv-v26-text-red font-bold text-xs">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full kbv-v26-bg-red opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full kbv-v26-bg-red" />
                </span>
                Live
              </span>
              <BoltKbvMatchMinute
                liveScore={liveScore}
                status={statusNum}
                matchTime={match?.matchTime}
                halfStartTime={match?.halfStartTime}
                className="text-[var(--theme-primary)] text-sm font-bold"
              />
            </div>
          ) : (
            <>
              <span className="text-sm font-bold text-[var(--theme-text)] leading-tight">{matchTimeStr}</span>
              <span className="text-sm font-normal text-[var(--theme-text)] leading-tight">{matchDateStr}</span>
            </>
          )}
        </div>

        <div className="flex flex-col justify-center gap-2 overflow-hidden h-full">
          <div className="flex items-center gap-1">
            <img src={homeLogo || ""} alt="" className="w-6 h-6 object-contain rounded-full bg-[var(--theme-accent)] p-0.5 shrink-0" />
            <span className="text-sm font-bold text-[var(--theme-text)] truncate flex-1">
              {match?.homeName}
            </span>
            {currentHomeScore !== null && (
              <span className="text-sm font-bold text-[var(--theme-primary)] shrink-0">{currentHomeScore}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <img src={awayLogo || ""} alt="" className="w-6 h-6 object-contain rounded-full bg-[var(--theme-accent)] p-0.5 shrink-0" />
            <span className="text-sm font-bold text-[var(--theme-text)] truncate flex-1">
              {match?.awayName}
            </span>
            {currentAwayScore !== null && (
              <span className="text-sm font-bold text-[var(--theme-primary)] shrink-0">{currentAwayScore}</span>
            )}
          </div>
        </div>

        <div className="flex flex-col justify-center gap-2 h-full uppercase">
          <div className="grid grid-cols-3 items-center gap-x-1 text-sm font-normal h-[28px]">
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(homeHandicapTrend, homeHandicapOdds)}`}>
              {homeHandicapOdds || "-"}
              {renderTrendArrow(homeHandicapTrend)}
            </span>
            <span className="min-w-[35px] text-center kbv-v26-odds-default">{handicapValue || "-"}</span>
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(awayHandicapTrend, awayHandicapOdds)}`}>
              {awayHandicapOdds || "-"}
              {renderTrendArrow(awayHandicapTrend)}
            </span>
          </div>
          <div className="grid grid-cols-3 items-center gap-x-1 text-sm font-normal h-[28px]">
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(homeHandicapHalfTrend, homeHandicapHalfOdds)}`}>
              {homeHandicapHalfOdds || "-"}
              {renderTrendArrow(homeHandicapHalfTrend)}
            </span>
            <span className="min-w-[35px] text-center kbv-v26-odds-default">{handicapHalfValue || "-"}</span>
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(awayHandicapHalfTrend, awayHandicapHalfOdds)}`}>
              {awayHandicapHalfOdds || "-"}
              {renderTrendArrow(awayHandicapHalfTrend)}
            </span>
          </div>
        </div>

        <div className="flex flex-col justify-center gap-2 h-full uppercase">
          <div className="grid grid-cols-3 items-center gap-x-1 text-sm font-normal h-[28px]">
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(overTrend, overOdds)}`}>
              {overOdds || "-"}
              {renderTrendArrow(overTrend)}
            </span>
            <span className="min-w-[35px] text-center kbv-v26-odds-default">{overUnderValue ? `${overUnderValue}` : "-"}</span>
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(underTrend, underOdds)}`}>
              {underOdds || "-"}
              {renderTrendArrow(underTrend)}
            </span>
          </div>
          <div className="grid grid-cols-3 items-center gap-x-1 text-sm font-normal h-[28px]">
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(overHalfTrend, overHalfOdds)}`}>
              {overHalfOdds || "-"}
              {renderTrendArrow(overHalfTrend)}
            </span>
            <span className="min-w-[35px] text-center kbv-v26-odds-default">{overUnderHalfValue ? `${overUnderHalfValue}` : "-"}</span>
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(underHalfTrend, underHalfOdds)}`}>
              {underHalfOdds || "-"}
              {renderTrendArrow(underHalfTrend)}
            </span>
          </div>
        </div>

        <div className="flex flex-col justify-center gap-2 h-full uppercase">
          <div className="grid grid-cols-3 items-center gap-x-1 text-sm font-normal h-[28px]">
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(homeEuropeTrend, homeEuropeOdds)}`}>
              {homeEuropeOdds || "-"}
              {renderTrendArrow(homeEuropeTrend)}
            </span>
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(drawTrend, instantDrawOdds)}`}>
              {instantDrawOdds || "-"}
              {renderTrendArrow(drawTrend)}
            </span>
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(awayEuropeTrend, awayEuropeOdds)}`}>
              {awayEuropeOdds || "-"}
              {renderTrendArrow(awayEuropeTrend)}
            </span>
          </div>
          <div className="grid grid-cols-3 items-center gap-x-1 text-sm font-normal h-[28px]">
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(homeEuropeHalfTrend, homeEuropeHalfOdds)}`}>
              {homeEuropeHalfOdds || "-"}
              {renderTrendArrow(homeEuropeHalfTrend)}
            </span>
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(drawHalfTrend, drawHalfOdds)}`}>
              {drawHalfOdds || "-"}
              {renderTrendArrow(drawHalfTrend)}
            </span>
            <span className={`min-w-[35px] flex items-center justify-start ${getFinalOddsColor(awayEuropeHalfTrend, awayEuropeHalfOdds)}`}>
              {awayEuropeHalfOdds || "-"}
              {renderTrendArrow(awayEuropeHalfTrend)}
            </span>
          </div>
        </div>

        <div className="h-full"></div>
      </div>

      <div className="hidden md:flex justify-end gap-2 px-2 pb-2">
        <button
          type="button"
          onClick={handleOpenTipsAiModal}
          className="kbv-v26-tips-ai-btn flex items-center gap-1.5 px-3 py-1 rounded-[4px] text-[var(--theme-on-primary)]"
        >
          <img src={viugoTipsAiSparkle} alt="" className="kbv-v26-tips-ai-sparkle h-3.5 w-3.5 shrink-0" />
          <span className="text-[11px] font-bold uppercase tracking-tight">Tips AI</span>
        </button>
        <div
          onClick={handleOtherOddsClick}
          className="kbv-v26-other-bets-btn flex items-center gap-1 bg-[var(--theme-primary)]/10 px-2 py-0.5 rounded-full border border-[var(--theme-primary)]/20 shadow-sm cursor-pointer transition-all"
        >
          <span className="text-sm font-bold text-[var(--theme-text-muted)] tracking-wide">
            {showOther ? "Thu gọn" : "Kèo khác"}
          </span>
          <img
            src={ICON_ARROW_UP}
            alt=""
            className={`kbv-v26-other-bets-arrow block w-5 h-5 opacity-70 transition-all ${showOther ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      <div className="flex md:hidden flex-col items-center justify-center py-[6px] w-full bg-transparent">
        <div className="kbv-v26-mobile-card w-full h-fit rounded-[9px] pb-0 flex flex-col relative overflow-hidden">
          <div className="flex justify-between items-center text-[var(--theme-text)] px-[2px] pt-[9px] pb-[4px] gap-[4px]">
            <div className="flex flex-col justify-center items-center w-[95px] h-auto">
              <img src={homeLogo} alt="logo" className="w-[26px] h-[26px] mb-[9px] object-contain" />
              <div className="kbv-v26-mobile-team text-sm font-bold leading-[18px] text-center break-all w-full relative line-clamp-2 text-[var(--theme-text)]">
                {match?.homeName || "Team"}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center text-sm font-bold min-w-[135px] leading-[18px] gap-[4px]">
              <div className="kbv-v26-mobile-league w-full text-center line-clamp-1 break-all uppercase text-[var(--theme-text-muted)]">
                {leagueName || match?.leagueName || "--"}
              </div>

              {isLive ? (
                <div className="flex justify-between items-center text-[var(--theme-text)] text-x1s font-normal max-w-[140px] w-full h-[20px] leading-4">
                  <div className="flex justify-center items-center kbv-v26-live-bg kbv-v26-live-text w-[20px] h-[20px] rounded-[2px] font-bold">{currentHomeScore ?? "-"}</div>
                  <div className="flex flex-col justify-center items-center gap-[1px] min-w-[43px] h-[20px]">
                    <BoltKbvMatchMinute
                      liveScore={liveScore}
                      status={statusNum}
                      matchTime={match?.matchTime}
                      halfStartTime={match?.halfStartTime}
                      className="kbv-v26-text-red text-sm font-bold leading-[18px]"
                    />
                  </div>
                  <div className="flex justify-center items-center kbv-v26-live-bg kbv-v26-live-text w-[20px] h-[20px] rounded-[2px] font-bold">{currentAwayScore ?? "-"}</div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-[var(--theme-text)] leading-[18px]">
                  <span className="kbv-v26-mobile-time text-sm font-bold">{matchTimeStr}</span>
                  <span className="kbv-v26-mobile-date text-xs font-normal text-[var(--theme-text-muted)]">{matchDateStr}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col justify-center items-center w-[95px] h-auto">
              <img src={awayLogo} alt="logo" className="w-[26px] h-[26px] mb-[9px] object-contain" />
              <div className="kbv-v26-mobile-team text-sm font-bold leading-[18px] text-center break-all w-full relative line-clamp-2 text-[var(--theme-text)]">
                {match?.awayName || "Team"}
              </div>
            </div>
          </div>

          <div className="kbv-v26-mobile-odds text-[var(--theme-text)] text-sm leading-5 px-[2px] pb-[9px] pt-[4px] flex flex-col">
            <div className="flex justify-between items-center mb-[4px]">
              <p className="kbv-v26-mobile-odds-label text-[var(--theme-text-muted)] text-x1s leading-[13px] w-[112px] max-[400px]:w-[100px] text-center m-0 p-0">Kèo Chấp (HT/FT)</p>
              <p className="kbv-v26-mobile-odds-label text-[var(--theme-text-muted)] text-x1s leading-[13px] w-[112px] max-[400px]:w-[100px] text-center m-0 p-0">Kèo Tài Xỉu (HT/FT)</p>
              <p className="kbv-v26-mobile-odds-label text-[var(--theme-text-muted)] text-x1s leading-[13px] w-[112px] max-[400px]:w-[100px] text-center m-0 p-0">1x2 (HT/FT)</p>
            </div>

            <div className="flex justify-between items-center mb-[4px]">
              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(homeHandicapTrend, homeHandicapOdds, getMobileOddsColor(homeHandicapOdds))}`}>
                  {homeHandicapOdds || "-"}
                  {renderTrendArrow(homeHandicapTrend)}
                </div>
                <div className="kbv-v26-odds-default text-center">{handicapValue || "-"}</div>
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(awayHandicapTrend, awayHandicapOdds, getMobileOddsColor(awayHandicapOdds))}`}>
                  {awayHandicapOdds || "-"}
                  {renderTrendArrow(awayHandicapTrend)}
                </div>
              </div>
              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(overTrend, overOdds, getMobileOddsColor(overOdds))}`}>
                  {overOdds || "-"}
                  {renderTrendArrow(overTrend)}
                </div>
                <div className="kbv-v26-odds-default text-center">{overUnderValue ? `${overUnderValue}` : "-"}</div>
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(underTrend, underOdds, getMobileOddsColor(underOdds))}`}>
                  {underOdds || "-"}
                  {renderTrendArrow(underTrend)}
                </div>
              </div>
              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(homeEuropeTrend, homeEuropeOdds, getMobileOddsColor(homeEuropeOdds))}`}>
                  {homeEuropeOdds || "-"}
                  {renderTrendArrow(homeEuropeTrend)}
                </div>
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(drawTrend, instantDrawOdds, getMobileOddsColor(instantDrawOdds))}`}>
                  {instantDrawOdds || "-"}
                  {renderTrendArrow(drawTrend)}
                </div>
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(awayEuropeTrend, awayEuropeOdds, getMobileOddsColor(awayEuropeOdds))}`}>
                  {awayEuropeOdds || "-"}
                  {renderTrendArrow(awayEuropeTrend)}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(homeHandicapHalfTrend, homeHandicapHalfOdds, getMobileOddsColor(homeHandicapHalfOdds))}`}>
                  {homeHandicapHalfOdds || "-"}
                  {renderTrendArrow(homeHandicapHalfTrend)}
                </div>
                <div className="kbv-v26-odds-default text-center">{handicapHalfValue || "-"}</div>
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(awayHandicapHalfTrend, awayHandicapHalfOdds, getMobileOddsColor(awayHandicapHalfOdds))}`}>
                  {awayHandicapHalfOdds || "-"}
                  {renderTrendArrow(awayHandicapHalfTrend)}
                </div>
              </div>

              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(overHalfTrend, overHalfOdds, getMobileOddsColor(overHalfOdds))}`}>
                  {overHalfOdds || "-"}
                  {renderTrendArrow(overHalfTrend)}
                </div>
                <div className="kbv-v26-odds-default text-center">{overUnderHalfValue ? `${overUnderHalfValue}` : "-"}</div>
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(underHalfTrend, underHalfOdds, getMobileOddsColor(underHalfOdds))}`}>
                  {underHalfOdds || "-"}
                  {renderTrendArrow(underHalfTrend)}
                </div>
              </div>

              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(homeEuropeHalfTrend, homeEuropeHalfOdds, getMobileOddsColor(homeEuropeHalfOdds))}`}>
                  {homeEuropeHalfOdds || "-"}
                  {renderTrendArrow(homeEuropeHalfTrend)}
                </div>
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(drawHalfTrend, drawHalfOdds, getMobileOddsColor(drawHalfOdds))}`}>
                  {drawHalfOdds || "-"}
                  {renderTrendArrow(drawHalfTrend)}
                </div>
                <div className={`flex items-center justify-center text-center ${getFinalOddsColor(awayEuropeHalfTrend, awayEuropeHalfOdds, getMobileOddsColor(awayEuropeHalfOdds))}`}>
                  {awayEuropeHalfOdds || "-"}
                  {renderTrendArrow(awayEuropeHalfTrend)}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 px-3 pb-2 pt-1 border-t border-[var(--theme-border)]">
            <button
              type="button"
              onClick={handleOpenTipsAiModal}
              className="kbv-v26-tips-ai-btn flex items-center gap-1 px-2 py-0.5 rounded-[4px] text-[var(--theme-on-primary)]"
            >
              <img src={viugoTipsAiSparkle} alt="" className="kbv-v26-tips-ai-sparkle h-3 w-3 shrink-0" />
              <span className="text-[10px] font-bold uppercase tracking-tight">Tips AI</span>
            </button>
            <div
              onClick={handleOtherOddsClick}
              className="kbv-v26-other-bets-btn flex items-center gap-1 bg-[var(--theme-primary)]/15 px-2 py-0.5 rounded-full border border-[var(--theme-primary)]/30 shadow-sm hover:scale-105 transition-all cursor-pointer"
            >
              <span className="text-[10px] font-bold text-[var(--theme-text)] tracking-wide">
                {showOther ? "Thu gọn" : "Kèo khác"}
              </span>
              <img
                src={ICON_ARROW_UP}
                alt=""
                className={`kbv-v26-other-bets-arrow block w-2.5 h-2.5 opacity-80 transition-transform ${showOther ? "rotate-180" : ""}`}
              />
            </div>
          </div>
        </div>
      </div>

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{ formattedDate: matchDateShort, formattedGio: matchTimeStr, isLive }}
        statsInfo={{
          currentHomeScore,
          currentAwayScore,
          homeYellow,
          homeRed,
          awayYellow,
          awayRed,
          currentHomeCorner,
          currentAwayCorner,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={derivedLeagueName}
        leagueLogo={derivedLeagueLogo}
        homeName={homeName}
        homeLogo={homeLogo}
        awayName={awayName}
        awayLogo={awayLogo}
        matchDate={matchDateShort}
        matchTime={matchTimeStr}
        matchTimestamp={match?.matchTime}
        headerScore={scoreText}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={modalStatusLabel}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />

      {showOther && (
        <BoltOtherBetsDropdown
          loadingOtherOdds={loadingOtherOdds}
          otherOddsData={otherOddsData}
          hasOtherData={hasOtherData}
          correctScoresListFull={correctScoresListFull}
          correctScoresFull={correctScoresFull}
          totalCornersOdds={totalCornersOdds}
          odds={{ ...filterOdds, ...liveOdds }}
          showOther={showOther}
        />
      )}
    </div>
  );
}
